import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  PlusIcon, 
  TrashIcon, 
  ChevronDownIcon,
  ChevronUpIcon 
} from '@heroicons/react/outline';

export default function CustomReportBuilder({ onApply, onCancel }) {
  const { t } = useLanguage();

  // Available fields from all entities
  const availableFields = [
    // Policy Fields
    { id: 'policyNumber', label: t('claims.policyNumber'), table: 'policy', type: 'text' },
    { id: 'policyType', label: t('claims.policyType'), table: 'policy', type: 'select', options: ['Third Party Liability', 'Comprehensive Car Insurance', 'Cargo Transport Insurance'] },
    { id: 'startDate', label: t('policy.startDate'), table: 'policy', type: 'date' },
    { id: 'endDate', label: t('policy.endDate'), table: 'policy', type: 'date' },
    { id: 'premium', label: t('claims.premium'), table: 'policy', type: 'number' },
    { id: 'totalPremium', label: t('policy.totalPremium'), table: 'policy', type: 'number' },
    { id: 'paymentStatus', label: t('policy.paymentStatus'), table: 'policy', type: 'select', options: ['paid', 'unpaid'] },
    { id: 'paymentMethod', label: t('policy.paymentMethod'), table: 'policy', type: 'select', options: ['cash', 'cheque', 'onAccount'] },
    { id: 'status', label: t('common.status'), table: 'policy', type: 'select', options: ['active', 'expired', 'cancelled', 'draft', 'void'] },
    
    // Client Fields
    { id: 'firstName', label: t('policies.firstName'), table: 'client', type: 'text' },
    { id: 'lastName', label: t('policies.lastName'), table: 'client', type: 'text' },
    { id: 'nationalId', label: t('client.nationalIdNumber'), table: 'client', type: 'text' },
    { id: 'mobile', label: t('client.mobileNumber'), table: 'client', type: 'text' },
    { id: 'email', label: t('common.email'), table: 'client', type: 'text' },
    { id: 'nationality', label: t('client.nationality'), table: 'client', type: 'text' },
    { id: 'address', label: t('common.address'), table: 'client', type: 'text' },
    
    // Vehicle Fields
    { id: 'carMake', label: t('policy.vehicleMake'), table: 'vehicle', type: 'text' },
    { id: 'carModel', label: t('policy.vehicleModel'), table: 'vehicle', type: 'text' },
    { id: 'carYear', label: t('policy.vehicleYear'), table: 'vehicle', type: 'number' },
    { id: 'plateNumber', label: t('policy.plateNumber'), table: 'vehicle', type: 'text' },
    { id: 'chassisNumber', label: t('policy.chassisNumber'), table: 'vehicle', type: 'text' },
    
    // Company/Branch Fields
    { id: 'companyName', label: t('common.company'), table: 'company', type: 'text' },
    { id: 'branchName', label: t('common.branch'), table: 'branch', type: 'text' },
    
    // Cargo Fields
    { id: 'cargoInsuranceSubType', label: t('cargo.transportType'), table: 'cargo', type: 'select', options: ['sea', 'road', 'air'] },
    { id: 'insuredName', label: t('cargo.insuredName'), table: 'cargo', type: 'text' },
    { id: 'cargoDescription', label: t('cargo.cargoDescription'), table: 'cargo', type: 'text' },
    { id: 'documentaryValue', label: t('cargo.documentaryValue'), table: 'cargo', type: 'number' },
    
    // Claim Fields
    { id: 'claimNumber', label: t('claims.claimNumber'), table: 'claim', type: 'text' },
    { id: 'claimStatus', label: t('claims.status'), table: 'claim', type: 'select', options: ['pending', 'approved', 'rejected', 'settled'] },
    { id: 'settlementAmount', label: t('claims.settlementAmount'), table: 'claim', type: 'number' },
    
    // Timestamps
    { id: 'createdAt', label: t('common.createdAt'), table: 'system', type: 'date' },
    { id: 'updatedAt', label: t('common.updatedAt'), table: 'system', type: 'date' }
  ];

  const [selectedColumns, setSelectedColumns] = useState([
    'policyNumber',
    'policyType',
    'firstName',
    'lastName',
    'totalPremium',
    'paymentStatus'
  ]);

  const [filters, setFilters] = useState([]);
  const [sortBy, setSortBy] = useState({ field: 'createdAt', direction: 'desc' });
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);

  // Toggle column selection
  const toggleColumn = (fieldId) => {
    if (selectedColumns.includes(fieldId)) {
      setSelectedColumns(selectedColumns.filter(id => id !== fieldId));
    } else {
      setSelectedColumns([...selectedColumns, fieldId]);
    }
  };

  // Add new filter
  const addFilter = () => {
    setFilters([
      ...filters,
      {
        id: Date.now(),
        field: 'policyNumber',
        operator: 'contains',
        value: ''
      }
    ]);
  };

  // Update filter
  const updateFilter = (filterId, key, value) => {
    setFilters(filters.map(f => 
      f.id === filterId ? { ...f, [key]: value } : f
    ));
  };

  // Remove filter
  const removeFilter = (filterId) => {
    setFilters(filters.filter(f => f.id !== filterId));
  };

  // Get field by id
  const getField = (fieldId) => {
    return availableFields.find(f => f.id === fieldId);
  };

  // Get operators for field type
  const getOperators = (fieldType) => {
    switch (fieldType) {
      case 'text':
        return [
          { value: 'contains', label: t('reports.contains') || 'Contains' },
          { value: 'equals', label: t('reports.equals') || 'Equals' },
          { value: 'startsWith', label: t('reports.startsWith') || 'Starts with' },
          { value: 'endsWith', label: t('reports.endsWith') || 'Ends with' }
        ];
      case 'number':
        return [
          { value: 'equals', label: t('reports.equals') || 'Equals' },
          { value: 'greaterThan', label: t('reports.greaterThan') || 'Greater than' },
          { value: 'lessThan', label: t('reports.lessThan') || 'Less than' },
          { value: 'between', label: t('reports.between') || 'Between' }
        ];
      case 'date':
        return [
          { value: 'equals', label: t('reports.equals') || 'Equals' },
          { value: 'after', label: t('reports.after') || 'After' },
          { value: 'before', label: t('reports.before') || 'Before' },
          { value: 'between', label: t('reports.between') || 'Between' }
        ];
      case 'select':
        return [
          { value: 'equals', label: t('reports.equals') || 'Equals' },
          { value: 'in', label: t('reports.in') || 'In' }
        ];
      default:
        return [{ value: 'equals', label: t('reports.equals') || 'Equals' }];
    }
  };

  // Apply report configuration
  const handleApply = () => {
    onApply({
      columns: selectedColumns,
      filters: filters,
      sortBy: sortBy
    });
  };

  // Group fields by table
  const groupedFields = availableFields.reduce((acc, field) => {
    if (!acc[field.table]) {
      acc[field.table] = [];
    }
    acc[field.table].push(field);
    return acc;
  }, {});

  const tableLabels = {
    policy: t('reports.policyFields') || 'Policy Fields',
    client: t('reports.clientFields') || 'Client Fields',
    vehicle: t('reports.vehicleFields') || 'Vehicle Fields',
    cargo: t('reports.cargoFields') || 'Cargo Fields',
    claim: t('reports.claimFields') || 'Claim Fields',
    company: t('reports.companyFields') || 'Company/Branch Fields',
    branch: t('reports.companyFields') || 'Company/Branch Fields',
    system: t('reports.systemFields') || 'System Fields'
  };

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 space-y-6">
      <div className="border-b pb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('reports.customReportBuilder') || 'Custom Report Builder'}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {t('reports.customReportDescription') || 'Select columns, add filters, and customize your report'}
        </p>
      </div>

      {/* Column Selection */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('reports.selectColumns') || 'Select Columns'}
          </h3>
          <button
            type="button"
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            {showColumnSelector ? (
              <>
                <ChevronUpIcon className="w-4 h-4 mr-1" />
                {t('common.hide') || 'Hide'}
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-4 h-4 mr-1" />
                {t('common.show') || 'Show'}
              </>
            )}
          </button>
        </div>

        <div className="text-sm text-gray-600">
          {selectedColumns.length} {t('reports.columnsSelected') || 'columns selected'}
        </div>

        {showColumnSelector && (
          <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
            {Object.entries(groupedFields).map(([table, fields]) => (
              <div key={table} className="mb-4">
                <h4 className="font-semibold text-gray-700 mb-2">{tableLabels[table]}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {fields.map(field => (
                    <label key={field.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(field.id)}
                        onChange={() => toggleColumn(field.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('reports.filters') || 'Filters'}
          </h3>
          <button
            type="button"
            onClick={addFilter}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            {t('reports.addFilter') || 'Add Filter'}
          </button>
        </div>

        {filters.length === 0 ? (
          <div className="text-sm text-gray-500 italic">
            {t('reports.noFilters') || 'No filters added yet'}
          </div>
        ) : (
          <div className="space-y-3">
            {filters.map(filter => {
              const field = getField(filter.field);
              const operators = getOperators(field?.type);
              
              return (
                <div key={filter.id} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  {/* Field Selection */}
                  <select
                    value={filter.field}
                    onChange={(e) => updateFilter(filter.id, 'field', e.target.value)}
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                  >
                    {availableFields.map(f => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </select>

                  {/* Operator Selection */}
                  <select
                    value={filter.operator}
                    onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                  >
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>

                  {/* Value Input */}
                  {field?.type === 'select' ? (
                    <select
                      value={filter.value}
                      onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      <option value="">{t('common.select') || 'Select'}</option>
                      {field.options?.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field?.type === 'number' ? 'number' : field?.type === 'date' ? 'date' : 'text'}
                      value={filter.value}
                      onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                      placeholder={t('reports.enterValue') || 'Enter value'}
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                  )}

                  {/* Remove Filter */}
                  <button
                    type="button"
                    onClick={() => removeFilter(filter.id)}
                    className="p-2 text-red-600 hover:text-red-700"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sorting */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('reports.sorting') || 'Sorting'}
        </h3>
        <div className="flex items-center space-x-3">
          <select
            value={sortBy.field}
            onChange={(e) => setSortBy({ ...sortBy, field: e.target.value })}
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
          >
            {availableFields.map(field => (
              <option key={field.id} value={field.id}>{field.label}</option>
            ))}
          </select>
          <select
            value={sortBy.direction}
            onChange={(e) => setSortBy({ ...sortBy, direction: e.target.value })}
            className="rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="asc">{t('reports.ascending') || 'Ascending'}</option>
            <option value="desc">{t('reports.descending') || 'Descending'}</option>
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          {t('common.cancel') || 'Cancel'}
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          {t('reports.generateReport') || 'Generate Report'}
        </button>
      </div>
    </div>
  );
}

